import React, { useContext } from 'react';
import classNames from 'classnames';
import { MobXProviderContext, observer } from 'mobx-react';
import { Button, Icon, Image, Loader } from 'semantic-ui-react';

import { SatCoreRegister } from '../SatCoreRegistry';

import NavigationService from '../services/NavigationService';
import PopupService from '../services/PopupService';
import UtilityService from '../services/UtilityService';

import { ELEMENT_TYPE } from '../managers/CourseManager';
import { PATH_TYPES } from '../managers/NavigationManager';

import iconArrowCircleRight from '../img/icon-arrow-circle-right.svg';

const VerticalCourseTOCHeader = observer((props) => {
  const { history, t } = props;
  const {
    classroomManager, courseManager, navigationManager, productManager, userManager, verticalCourseTOCManager
  } = useContext(MobXProviderContext);

  const { currentClassroomId } = classroomManager;
  const { currentCourseName, currentElementId, fullCourseResource } = courseManager;
  const { pathList } = navigationManager;
  const { isStudent } = userManager;

  const rootPath = pathList?.find((path) => path.type === PATH_TYPES.ROOT);
  const effectiveCourseName = currentCourseName || rootPath?.name || '';
  const classroomName = classroomManager.getClassName(currentClassroomId, { shouldReturnParserWrapperObj: true })?.stripped || '';
  const backToClassroomCoursesLabel = classroomName ? (
    t('backToClassroomCoursesLabel', { classroomName })
  ) : t('backToCoursesLabel');

  const handleClickBackToClassroomCourses = async (event) => {
    // Prevent the back button click from also firing the header's course-root click.
    event?.stopPropagation();
    const rootIndex = pathList?.findIndex((path) => path.type === PATH_TYPES.ROOT);
    if (!(rootIndex > 0)) {
      return;
    }
    const classroomPath = pathList[rootIndex - 1];
    NavigationService.navigateToPath(
      classroomPath, history, courseManager, isStudent, classroomManager
    );
  };

  const handleClickCourseHeader = async () => {
    // Simulate clicking the course-root breadcrumb — reuses the exact path
    // object added by buildReFreshPaths (for identical nav flow).
    if (!rootPath) {
      return;
    }
    const { isDistrictOrSchoolAdmin, isDistrictOrSchoolAssessmentAdmin, isProductAdmin } = userManager;
    const isAdmin = isDistrictOrSchoolAdmin || isDistrictOrSchoolAssessmentAdmin || isProductAdmin;
    await NavigationService.navigateToPath(
      rootPath, history, courseManager, isStudent, classroomManager, isAdmin
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClickCourseHeader();
    }
  };

  // Only show the header as selected once the tree has loaded AND the vertical
  // TOC is done loading, to avoid a brief "selected then unselected" flash
  // while a saved-location restore is in flight on card-click entry.
  const isCourseHeaderSelected = !!fullCourseResource
    && !verticalCourseTOCManager.isLoadingVerticalCourseTOC
    && (!currentElementId || currentElementId === ELEMENT_TYPE.ROOT)
    && !!effectiveCourseName;

  const courseHeaderContentClassName = classNames('vertical-course-toc-header-content', {
    selected: isCourseHeaderSelected
  });

  return (
    <div className='vertical-course-toc-header'>
      <div
        aria-current={isCourseHeaderSelected ? 'page' : undefined}
        className={courseHeaderContentClassName}
        onClick={handleClickCourseHeader}
        onKeyDown={handleKeyDown}
        role='button'
        tabIndex={0}>
        {!productManager.isFromProduct && PopupService.renderPopup({
          className: 'custom-popup custom-popup-curved',
          content: (
            <div className='vertical-course-toc-back-button-popup-content'>
              {backToClassroomCoursesLabel}
            </div>
          ),
          key: 'vertical-course-toc-back-button-popup',
          mouseEnterDelay: 500,
          position: 'top left',
          trigger: (
            <Button
              aria-label={backToClassroomCoursesLabel}
              className='vertical-course-toc-back-button'
              onClick={handleClickBackToClassroomCourses}
              primary
              type='button'>
              <Icon aria-hidden='true' name='chevron left' />
              <span className='vertical-course-toc-back-button-label'>{t('back')}</span>
            </Button>
          )
        })}
        {(verticalCourseTOCManager.isLoadingVerticalCourseTOC || !effectiveCourseName) ? (
          <div className='vertical-course-toc-course-title vertical-course-toc-loading'>
            <span>{t('loading')}</span>
            <Loader active inline size='mini' />
          </div>
        ) : (
          PopupService.renderPopup({
            content: (
              <div className='vertical-course-toc-course-title-popup-content'>
                {UtilityService.reactHtmlParserWrapper(effectiveCourseName).parsedNoPWrap}
              </div>
            ),
            key: 'vertical-course-toc-course-title-popup',
            mouseEnterDelay: 1000,
            position: 'top left',
            trigger: (
              <div className='vertical-course-toc-course-title'>
                {UtilityService.reactHtmlParserWrapper(effectiveCourseName).parsedNoPWrap}
              </div>
            )
          })
        )}
        <Image alt='' aria-hidden='true' className='row-arrow' src={iconArrowCircleRight} />
      </div>
    </div>
  );
});

export default VerticalCourseTOCHeader;
SatCoreRegister('VerticalCourseTOCHeader', VerticalCourseTOCHeader);
